export interface IReverbSettings {
	dryGain: number; // (0 to 1) Volume of the dry signal
	wetGain: number; // (0 to 1) Volume of the wet signal
	earlyDelay: number; // (0 to 0.3) Delay from dry signal before early reflections (aka preDelay)
	density: number; // (0 to 20) Number of early reflections per second
	lateDelay: number; // (0 to 0.5) Delay from dry signal to start of tail, and end of early reflections
	decayTime: number; // (0 to 5) Time from earlyDelay to end of tail
	hfCutoff: number; // (0 to 20,000) Cutoff for high frequencies
	hfDecay: number; // (0 to 1) How fast high frequencies decay compared to lower frequencies
}

// This reverb effect starts off the with the source signal (aka the dry signal).
// After a delay (early delay) it plays some early reflections using delay nodes.
// The number of early reflections is determined using the density setting and the
// time between the early and late delays.
// After the second delay (late delay) the tail of the reverb starts using 2 convolver nodes.
// One is for the low/mid frequencies (below hfCutoff) which linearly fades out over the decayTime.
// The other is for high frquencies (above hfCutoff) which can fade out sooner using the hfDecay setting.
export class ReverbEffect {
	public inputNode: GainNode;

	private audioContext: BaseAudioContext;
	private dryGainNode: GainNode;
	private wetGainNode: GainNode;
	private earlyDelayNode: DelayNode;
	private highPassFilterNode: BiquadFilterNode;
	private lowPassFilterNode: BiquadFilterNode;
	private lfConvolverNode: ConvolverNode;
	private hfConvolverNode: ConvolverNode;
	private earlyRefelectionNodes: { delayNode: DelayNode; gainNode: GainNode }[];

	constructor(audioContext: BaseAudioContext, settings: IReverbSettings) {
		this.audioContext = audioContext;

		this.inputNode = audioContext.createGain();
		this.dryGainNode = audioContext.createGain();
		this.wetGainNode = audioContext.createGain();
		this.earlyDelayNode = audioContext.createDelay();
		this.lowPassFilterNode = audioContext.createBiquadFilter();
		this.highPassFilterNode = audioContext.createBiquadFilter();
		this.lfConvolverNode = audioContext.createConvolver();
		this.hfConvolverNode = audioContext.createConvolver();
		this.earlyRefelectionNodes = [];

		this.inputNode.connect(this.dryGainNode);
		this.inputNode.connect(this.earlyDelayNode);
		this.earlyDelayNode.connect(this.lowPassFilterNode);
		this.earlyDelayNode.connect(this.highPassFilterNode);
		this.lowPassFilterNode.connect(this.lfConvolverNode);
		this.highPassFilterNode.connect(this.hfConvolverNode);
		this.lfConvolverNode.connect(this.wetGainNode);
		this.hfConvolverNode.connect(this.wetGainNode);

		this.update(settings);
	}

	update(settings: IReverbSettings) {
		this.dryGainNode.gain.value = settings.dryGain;
		this.wetGainNode.gain.value = settings.wetGain;
		this.earlyDelayNode.delayTime.value = settings.earlyDelay;

		// Early Reflections
		for (let earlyReflectionNode of this.earlyRefelectionNodes) {
			this.earlyDelayNode.disconnect(earlyReflectionNode.delayNode);
			earlyReflectionNode.delayNode.disconnect();
			earlyReflectionNode.gainNode.disconnect();
		}

		this.earlyRefelectionNodes = [];

		const earlyReflectionCount =
			settings.decayTime === 0
				? 0
				: Math.ceil((settings.lateDelay - settings.earlyDelay) * settings.density);
		for (let i = 0; i < earlyReflectionCount; i++) {
			let delayTime = (i / earlyReflectionCount) * (settings.lateDelay - settings.earlyDelay);

			let delayNode = this.audioContext.createDelay();
			delayNode.delayTime.value = delayTime;
			let gainNode = this.audioContext.createGain();
			gainNode.gain.value = 1 - delayTime / settings.decayTime;

			this.earlyDelayNode.connect(delayNode);
			delayNode.connect(gainNode);
			gainNode.connect(this.wetGainNode);
			this.earlyRefelectionNodes.push({ delayNode: delayNode, gainNode: gainNode });
		}

		// Reverb Tail
		const tailStartSample =
			this.audioContext.sampleRate * (settings.lateDelay - settings.earlyDelay);

		// Low Frequencies Reverb Tail
		this.lowPassFilterNode.type = 'lowpass';
		this.lowPassFilterNode.frequency.value = settings.hfCutoff;

		const lfImpulseResponseSamples = this.audioContext.sampleRate * settings.decayTime;
		if (lfImpulseResponseSamples > 0) {
			let lfImpulseResponseBuffer = this.audioContext.createBuffer(
				2,
				lfImpulseResponseSamples,
				this.audioContext.sampleRate
			);
			for (let i = 0; i < 2; i++) {
				let channelData = lfImpulseResponseBuffer.getChannelData(i);
				for (let sampleIdx = 0; sampleIdx < channelData.length; sampleIdx++) {
					if (sampleIdx < tailStartSample) {
						channelData[sampleIdx] = 0;
					} else {
						channelData[sampleIdx] =
							(Math.random() * 2 - 1) * (1 - sampleIdx / channelData.length);
					}
				}
			}

			this.lfConvolverNode.buffer = lfImpulseResponseBuffer;

			this.lfConvolverNode.disconnect();
			this.lfConvolverNode.connect(this.wetGainNode);
		} else {
			this.lfConvolverNode.disconnect();
		}

		// High Frequency Reverb Tail
		this.highPassFilterNode.type = 'highpass';
		this.highPassFilterNode.frequency.value = settings.hfCutoff;

		const hfImpulseResponseSamples =
			this.audioContext.sampleRate * settings.decayTime * settings.hfDecay;
		if (hfImpulseResponseSamples > 0) {
			let hfImpulseResponseBuffer = this.audioContext.createBuffer(
				2,
				hfImpulseResponseSamples,
				this.audioContext.sampleRate
			);
			for (let i = 0; i < 2; i++) {
				let channelData = hfImpulseResponseBuffer.getChannelData(i);
				for (let sampleIdx = 0; sampleIdx < channelData.length; sampleIdx++) {
					if (sampleIdx < tailStartSample) {
						channelData[sampleIdx] = 0;
					} else {
						channelData[sampleIdx] =
							(Math.random() * 2 - 1) * (1 - sampleIdx / channelData.length);
					}
				}
			}

			this.hfConvolverNode.buffer = hfImpulseResponseBuffer;

			this.hfConvolverNode.disconnect();
			this.hfConvolverNode.connect(this.wetGainNode);
		} else {
			this.hfConvolverNode.disconnect();
		}
	}

	connect(outputAudioNode: AudioNode) {
		this.dryGainNode.connect(outputAudioNode);
		this.wetGainNode.connect(outputAudioNode);
	}
}
